import React, {useEffect, useMemo, useState, useRef} from 'react'
import {Container} from 'styled-bootstrap-grid'
import {useRouter} from 'next/router'
import type {
  CollectionQuery,
  Collection,
  GetAttributesQuery,
  GetAttributesQueryVariables,
} from 'src/api/saleor/generated'
import {
  ProductDocument,
  CollectionDocument,
  useCollectionQuery,
  GetAttributesDocument,
} from 'src/api/saleor/generated'
import type {QueryListenerOptions} from 'react-datocms'
// import { Page_landingPage, LandingPage } from 'src/api/datocms/types/LandingPage'
import PageContainer from 'src/components/layout/PageContainer'
import {makeSubscriptionProps} from 'src/api/datocms/utils'
import {
  collectionLandingPageQuery,
  collectionPageQuery,
} from 'src/api/datocms/queries'
import type {
  CollectionLandingPage,
  Page_collectionLandingPage,
} from 'src/api/datocms/types/CollectionLandingPage'
import CollectionPage from 'src/components/collection/CollectionPage'
import {Loader, PaginationIndicator} from 'src/components/common'
import {Breadcrumbs} from 'src/components/products'
import {paths} from 'src/utils/paths'
import {serverPropsRender} from 'src/utils/serverRender/serverPropsRender'
import type {RvProduct} from 'src/api/local'
import {fetchFnServer} from 'src/api/local'
import {PageContent, PageLoading} from 'src/components/genericPage'
import {DatocmsContent} from 'src/components/content/DatocmsContent'
import {H2} from 'src/components/typography'
import {isSSR} from 'src/utils/isSSR'
import type {Maybe} from 'src/utils/Maybe'
import type {InMemoryCache} from '@apollo/client'
import styled from 'styled-components'
import {useQueryWithCache} from 'src/utils/gqlQuery/gqlQuery'
import {sortingArgsMap} from 'src/utils/products'
// interface Props {
//   pageSubscription: {
//     enabled: boolean
//     initialData: LandingPage
//   }
// }

// type landingPageType = QueryListenerOptions<
//   LandingPage,
//   {id: string}
// > | null

interface Ref {
  current: number
}

type landingPageType = QueryListenerOptions<
  CollectionLandingPage,
  {collectionId: string}
> | null

interface Props {
  pageSubscription: {
    enabled: boolean
    initialData: CollectionLandingPage
  }
}

type Filters = 'new' | 'price_asc' | 'alph_asc'

// eslint-disable-next-line unused-imports/no-unused-vars
interface SortObject {
  field: string
  direction: string
}
interface Attributes {
  slug: string
  values?: string[]
}
interface EcoFilter {
  attributes: [Attributes]
}

const LoaderContainer = styled.div`
  width: 100%;
  display: grid;
  place-items: center;
`
const _PAGE_LIMIT = 24

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setUndefinedNull = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any> | undefined,
): void => {
  if (obj) {
    Object.keys(obj).forEach((key: string) => {
      if (obj[key] && typeof obj[key] === 'object') {
        setUndefinedNull(obj[key])
      } else if (typeof obj[key] === 'undefined') {
        obj[key] = null // eslint-disable-line no-param-reassign
      }
    })
  }
}

export const getServerSideProps = serverPropsRender({
  query: async ({query, getProp, context, throwNotFound}) => {
    const {data: attrData} = await query({
      query: GetAttributesDocument,
      variables: {
        slug: 'certifications-eco',
      },
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, unused-imports/no-unused-vars
    const envArr = attrData?.attribute?.choices?.edges.map((choice: any) =>
      choice.node.slug ? choice.node.slug : null,
    )
    const {data, error} = await query({
      query: CollectionDocument,
      context: {
        nonAuthorized: true,
      },
      variables: {
        id: getProp(context, {key: 'id'}),
        first: _PAGE_LIMIT,
        after: null,
      },
    })
    const json = data as CollectionQuery | null
    if (!json || !json.collection || !json.collection.id || error) {
      throwNotFound()
    }
    return data
  },
  data: async ({context, client, query}) => {
    const {slug} = context.params || {slug: undefined}
    if (Array.isArray(slug)) {
      return {}
    }
    // Fake storage for Apollo
    const {cache} = client
    const cacheData = cache.extract() as Record<
      string,
      {__typename: string; slug: string}
    >
    const slugs = Object.keys(cacheData)
      .filter(
        (key) => cacheData[key] && cacheData[key].__typename === 'Product',
      )
      .map((key) => cacheData[key].slug)
    const obj: Record<string, RvProduct> = {}
    await Promise.all(
      slugs.map(async (itemSlug) => {
        await query({
          query: ProductDocument,
          variables: {
            slug: itemSlug,
          },
        })
        const data = fetchFnServer(itemSlug, cache as InMemoryCache) ?? null
        if (data?.slug) {
          obj[data.slug] = data
        }
      }),
    )
    setUndefinedNull(obj)
    return obj
  },
  // generateInitProps: async ({ context }) => ({
  //   pageSubscription: await makeSubscriptionProps(
  //     context.preview ?? false,
  //     pageQuery,
  //     false,
  //   ),
  // }),
  generateInitProps: async ({context}) => ({
    pageSubscription: await makeSubscriptionProps(
      context.preview ?? false,
      collectionPageQuery,
      {collectionId: context.params?.id},
      false,
    ),
  }),
})

const Vorusafn = ({pageSubscription}: Props): JSX.Element => {
  const router = useRouter()
  const id = Array.isArray(router.query.id)
    ? router.query.id[0]
    : router.query.id

  let ref: Ref = useMemo(() => ({current: 0}), [])
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ref = useRef(Number(sessionStorage.getItem(`scrollPosition_${id}`)))
  }

  const [sortOpt, setSortOpt] = useState<Filters>(
    () => (router.query.sort as Filters) || 'alph_asc',
  )
  const [envFilter, setEnvFilter] = useState<boolean>(() =>
    // eslint-disable-next-line no-unneeded-ternary
    router?.query?.ecofriendly ? true : false,
  )
  const [showLoader, setShowLoader] = useState<boolean>(false)
  const [showMoreLoader, setShowMoreLoader] = useState<boolean>(false)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleScroll = () => {
      sessionStorage.setItem(`scrollPosition_${id}`, `${window.scrollY}`)
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [id])

  const [landingPage, setLandingPage] = useState<
    Page_collectionLandingPage | null | undefined
  >(null)

  useEffect(() => {
    if (
      pageSubscription?.initialData?.collectionLandingPage &&
      pageSubscription?.initialData?.collectionLandingPage.collectionId === id
    ) {
      setLandingPage(pageSubscription?.initialData?.collectionLandingPage)
    } else {
      makeSubscriptionProps(
        false,
        collectionLandingPageQuery,
        {collectionId: id},
        true,
      )
        .catch((err) => {
          console.log({err})
        })
        .then((data) => {
          setLandingPage(
            (data as landingPageType)?.initialData?.collectionLandingPage,
          )
          return Promise.resolve()
        })
        .then(() => window.scrollTo(0, +ref.current))
    }
  }, [
    pageSubscription?.initialData?.collectionLandingPage,
    id,
    landingPage,
    ref,
  ])

  const deleteUrlParams = (key: string): void => {
    const updateUrl = new URL(window.location.href)
    const params = new URLSearchParams(updateUrl.search)
    params.delete(key)
    router.push(`${window.location.pathname}?${params.toString()}`, undefined, {
      shallow: true,
    })
  }

  useEffect(() => {
    if (!router.query.sort) {
      setSortOpt('alph_asc')
      window.scrollTo(0, ref.current)
    } else if (router.query.sort) {
      setSortOpt(router.query.sort as Filters)
      window.scrollTo(0, ref.current)
    }
  }, [router?.query, ref])

  const AddUrlParams = ({
    key,
    value,
  }: {
    key: string
    value: string | null
  }): void => {
    // eslint-disable-next-line no-restricted-globals
    const updateUrl = new URL(window.location.href)
    const params = new URLSearchParams(updateUrl.search)
    params.set(key, value ?? '')
    router.push(`${window.location.pathname}?${params.toString()}`, undefined, {
      shallow: true,
    })
  }

  const {data: attributeData} = useQueryWithCache<
    GetAttributesQuery,
    GetAttributesQueryVariables
  >(GetAttributesDocument, {
    fetchPolicy: isSSR ? 'cache-only' : 'cache-first',
    variables: {
      slug: 'certifications-eco',
    },
  })

  const envAttrValues: string[] = useMemo(() => {
    const attrArr: string[] = []
    attributeData?.attribute?.choices?.edges.forEach((choice) =>
      choice.node.slug ? attrArr.push(choice.node.slug) : null,
    )
    return attrArr
  }, [attributeData])

  let ecoFilter: EcoFilter

  if (router.query.ecofriendly) {
    ecoFilter = {
      attributes: [{slug: 'certifications-eco', values: [...envAttrValues]}],
    }
  } else {
    ecoFilter = {attributes: [{slug: 'certifications-eco'}]}
  }
  const {data, loading, fetchMore, refetch} = useCollectionQuery({
    query: CollectionDocument,
    context: {
      nonAuthorized: true,
    },
    skip: id == null,
    variables: {
      id: id ?? '',
      first: 24,
      after: null,
    },
  })
  useEffect(() => {
    if (envFilter) {
      // window.scrollTo(0, 0)
      AddUrlParams({key: 'ecofriendly', value: 'true'})
      setShowLoader(true)
      refetch({
        sortBy: sortingArgsMap[sortOpt][0],
        filter: {
          attributes: [
            {
              slug: 'certifications-eco',
              values: [...envAttrValues],
            },
          ],
        },
      })
        .then(() => setShowLoader(false))
        .then(() => window.scrollTo(0, ref.current))
    } else {
      deleteUrlParams('ecofriendly')
      setShowLoader(true)
      refetch({
        sortBy: sortingArgsMap[sortOpt][0],
        filter: {attributes: [{slug: 'certifications-eco'}]},
      })
        .then(() => setShowLoader(false))
        .then(() => window.scrollTo(0, ref.current))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFilter])

  const fetchMoreProducts = (): void => {
    if (data?.collection?.products?.pageInfo?.hasNextPage)
      setShowMoreLoader(true)
    fetchMore({
      query: CollectionDocument,
      variables: {
        id,
        first: 24,
        after: data?.collection?.products?.pageInfo?.endCursor,
        sortBy: sortingArgsMap[sortOpt][0],
        filter: ecoFilter,
      },
    }).then(() => setShowMoreLoader(false))
  }
  if (!isSSR && loading) {
    return <PageLoading />
  }
  if ((!data || !data.collection) && !loading) {
    return (
      <PageContent label="Vörusafn finnst ekki">
        <Container>
          <H2>Vörusafn finnst ekki</H2>
        </Container>
      </PageContent>
    )
  }

  const handleChange = (filter: Filters): void => {
    setShowLoader(true)
    refetch({sortBy: sortingArgsMap[filter][0]}).then(() =>
      setShowLoader(false),
    )
    setSortOpt(filter)
    AddUrlParams({key: 'sort', value: filter})
  }

  return (
    <PageContainer title={`${data?.collection?.name} | Rekstrarvörur`}>
      <Container>
        <Breadcrumbs
          breadcrumbs={[
            {title: 'Forsíða', href: paths.home},
            {title: data?.collection?.name, href: router.asPath},
          ]}
        />
        {landingPage?.content && (
          <DatocmsContent content={landingPage.content} />
        )}
        <CollectionPage
          collection={data?.collection as Collection}
          checked={envFilter}
          onClick={() => setEnvFilter(!envFilter)}
          sortClass={sortOpt}
          handleChange={handleChange}
          showLoading={showLoader}
        />
        {showMoreLoader ? (
          <LoaderContainer>
            <Loader loaderKey={id} size={24} />
          </LoaderContainer>
        ) : (
          <Vorusafn.PaginInationIndicator
            currentCount={data?.collection?.products?.edges?.length}
            totalCount={data?.collection?.products?.totalCount}
            onClickMore={fetchMoreProducts}
            loading={loading}
          />
        )}
      </Container>
    </PageContainer>
  )
}

interface PaginationIndicatorProps {
  loading: Maybe<boolean>
  currentCount: Maybe<number>
  totalCount: Maybe<number>
  onClickMore: () => void
}

Vorusafn.PaginInationIndicator = ({
  loading,
  currentCount,
  totalCount,
  onClickMore,
}: PaginationIndicatorProps) => {
  if (loading || !currentCount || !totalCount) {
    return null
  }
  return (
    <PaginationIndicator
      currentCount={currentCount}
      totalCount={totalCount}
      onClickMore={onClickMore}
    />
  )
}

export default Vorusafn
